package com.eladfinish.bible.contest.screens.randomVerse

import androidx.compose.runtime.Immutable
import androidx.compose.runtime.Stable
import com.eladfinish.bible.contest.arch.ActionKt
import com.eladfinish.bible.contest.arch.EffectKt
import com.eladfinish.bible.contest.arch.StateKt
import com.eladfinish.bible.contest.data.ChapterData
import com.eladfinish.bible.contest.domain.catalog.BookData
import com.eladfinish.bible.contest.extensions.toAnnotatedStringWithContinuousUnderlined
import com.eladfinish.bible.contest.screens.randomVerse.model.RandomVersePart
import com.eladfinish.bible.contest.screens.randomVerse.model.RandomizedVerse
import com.eladfinish.bible.contest.screens.search.model.VerseUiModel
import com.eladfinish.bible.contest.text.SpannableStringKMP
import com.eladfinish.bible.contest.ui.menus.actions.RandomVerseToolbarAction
import kotlinx.collections.immutable.ImmutableList

@Immutable
data class RandomVerseState(
    val bookItems: ImmutableList<Pair<BookData, Boolean>>,
    val verseUiModel: VerseUiModel?,
    val randomizedVerse: RandomizedVerse,
    val shownPart: RandomVersePart?,
    val dialogState: DialogState,
    val bottomSheetState: BottomSheetState,
    val isShakeEnabled: Boolean,
    val prefsFingerprint: Int = 0,
    /** The small hint that offers the home-screen widget is on the screen (see VerseWidgetPromptGate). */
    val isWidgetPromptVisible: Boolean = false,
) : StateKt {

    val spannableStringKMP
        get() = when (shownPart) {
            RandomVersePart.HALF -> randomizedVerse.halfSpannableStringKMP
            RandomVersePart.FULL -> randomizedVerse.fullSpannableStringKMP
            null -> randomizedVerse.halfSpannableStringKMP
        }

    val randomizedVerseAsAnnotatedString
        get() = spannableStringKMP.toAnnotatedStringWithContinuousUnderlined()

    @Stable
    sealed class DialogState {
        data class ChapterDialog(
            val chapterData: ChapterData,
        ) : DialogState()

        data object None : DialogState()
    }

    @Stable
    sealed class BottomSheetState {
        data class ChapterPicker(
            val content: SpannableStringKMP,
        ) : BottomSheetState()

        data object Hidden : BottomSheetState()
    }
}

sealed class RandomVerseAction : ActionKt {
    data class ToolbarClicked(val toolbarAction: RandomVerseToolbarAction) : RandomVerseAction()

    data object VerseClicked : RandomVerseAction()
    data object VerseLongClicked : RandomVerseAction()
    data object Refresh : RandomVerseAction()
    data object SettingsClicked : RandomVerseAction()
    data object MultipleRandomVersesClicked : RandomVerseAction()
    data object DeviceIsShaken : RandomVerseAction()
    data object ParchmentClicked : RandomVerseAction()
    data object MikudClicked : RandomVerseAction()
    data object WrongAnswerChosen : RandomVerseAction()
    data class BookClicked(val name: String) : RandomVerseAction()
    data class ChapterClicked(val isCorrect: Boolean) : RandomVerseAction()
    data object HideDialog : RandomVerseAction()
    data object HideBottomSheet : RandomVerseAction()
    data object RefreshAfterSettings : RandomVerseAction()
    data object WidgetPromptDismissed : RandomVerseAction()
    data object WidgetPromptClicked : RandomVerseAction()
}

sealed class RandomVerseEffect : EffectKt {
    data class CopyToClipboard(val text: String) : RandomVerseEffect()
    data object NotifyWrongAnswer : RandomVerseEffect()
    data object NotifyWrongAnswerOnChapter : RandomVerseEffect()
    data object NotifyCorrectAnswer : RandomVerseEffect()
    data object NotifyCorrectAnswerOnChapter : RandomVerseEffect()
    data object NotifyEmptyMikud : RandomVerseEffect()
    data object OpenSeveralRandomVerses : RandomVerseEffect()
    data object OpenRandomVerseSettings : RandomVerseEffect()
    data object DismissSnackbar : RandomVerseEffect()
    data object OpenVerseWidget : RandomVerseEffect()
}
